import { useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Calendar,
  Clock,
  MapPin,
  ArrowLeft,
  ArrowUpRight,
  Accessibility,
  Languages,
  ShieldAlert,
  ExternalLink,
  Sparkles,
} from "lucide-react";
import { Container, FadeIn, SectionLabel, SectionHeading } from "@/components/Section";
import SEOHead from "@/components/SEOHead";
import { getShow, getAllShows } from "@/lib/shows";

export default function ShowDetail() {
  const { slug } = useParams();
  const show = useMemo(() => getShow(slug), [slug]);

  if (!show) {
    return (
      <div
        data-testid="show-not-found"
        className="min-h-screen pt-32 pb-20 px-6 text-center"
      >
        <SEOHead
          title="Show not found · Temple of Kulture"
          description="That show isn't in our universe yet — step back into the Temple."
        />
        <p className="text-amber-400 text-[11px] uppercase tracking-[0.35em] font-bold mb-6">
          Lost the thread
        </p>
        <h1 className="font-display font-black uppercase text-5xl">Show not found</h1>
        <Link
          to="/"
          className="mt-10 inline-flex items-center bg-amber-400 hover:bg-amber-300 text-black px-8 py-5 font-bold uppercase tracking-[0.25em] text-xs"
        >
          ← Return home
        </Link>
      </div>
    );
  }

  const related = (show.related || [])
    .map((s) => getShow(s))
    .filter(Boolean)
    .slice(0, 3);

  const BookCta = () => {
    const cls =
      "inline-flex items-center justify-center gap-3 bg-amber-400 hover:bg-amber-300 text-black px-7 py-5 font-bold uppercase tracking-[0.25em] text-xs transition-colors";
    if (show.bookingType === "external") {
      return (
        <a
          href={show.bookingUrl}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="show-book-cta"
          className={cls}
        >
          {show.bookingCtaLabel} <ExternalLink className="h-4 w-4" />
        </a>
      );
    }
    return (
      <Link
        to={show.bookingUrl || "/corporate"}
        data-testid="show-book-cta"
        className={cls}
      >
        {show.bookingCtaLabel || "Enquire"} <ArrowUpRight className="h-4 w-4" />
      </Link>
    );
  };

  return (
    <div data-testid="show-detail-page">
      <SEOHead
        title={`${show.title} · ${show.eyebrow || "Immersive Show"} · Temple of Kulture`}
        description={`${show.tagline || show.title}. ${show.runDatesShort ? show.runDatesShort + " · " : ""}${show.venue || ""}.`.trim()}
        image={show.heroImage}
        type="event"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "TheaterEvent",
          name: show.title,
          description: show.tagline,
          image: show.heroImage,
          startDate: show.startDate || undefined,
          location: show.venue ? { "@type": "Place", name: show.venue, address: show.venueAddress } : undefined,
          organizer: { "@type": "Organization", name: "Temple of Kulture" },
        }}
      />
      {/* HERO — cinematic, full-bleed */}
      <section className="relative min-h-[80vh] flex items-end overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center scale-105"
          style={{ backgroundImage: `url(${show.heroImage})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/75 to-[#050505]/30" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/30 to-black/20" />

        <Container className="relative pt-40 pb-20 w-full">
          <Link
            to="/"
            data-testid="show-back-link"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] font-bold text-amber-400 hover:gap-3 transition-all mb-10"
          >
            <ArrowLeft className="h-4 w-4" /> Back to the Temple
          </Link>
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: "easeOut" }}
            className="max-w-4xl"
          >
            <p className="text-[11px] uppercase tracking-[0.4em] text-amber-400 font-bold mb-6 flex items-center gap-3">
              <span className="inline-block h-2 w-2 bg-amber-400 tok-pulse-gold" />
              {show.eyebrow}
            </p>
            <h1
              className="tok-headline-lg"
              data-testid="show-detail-title"
            >
              {show.title}
            </h1>
            <p className="mt-8 text-lg sm:text-2xl text-zinc-200 max-w-3xl leading-relaxed font-display">
              {show.tagline}
            </p>
            <div className="mt-10 flex flex-wrap gap-4 items-center">
              <BookCta />
              <span className="text-[11px] uppercase tracking-[0.3em] text-amber-400 font-bold">
                {show.status}
              </span>
            </div>
          </motion.div>
        </Container>
      </section>

      {/* KEY DETAILS STRIP */}
      <section className="relative bg-amber-400 text-black">
        <Container className="py-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 sm:gap-10">
            <StripCell Icon={Calendar} label="Dates" value={show.runDatesShort} />
            <StripCell Icon={MapPin} label="Venue" value={show.venue} />
            <StripCell Icon={Clock} label="Run time" value={show.runTime} />
            <StripCell Icon={Languages} label="Language" value={show.language} />
          </div>
        </Container>
      </section>

      {/* SYNOPSIS + POSTER */}
      <section className="py-24 sm:py-32">
        <Container>
          <div className="grid lg:grid-cols-12 gap-12">
            <div className="lg:col-span-7 space-y-14">
              <FadeIn>
                <SectionLabel>Synopsis</SectionLabel>
                <SectionHeading className="mt-6">
                  A story <br />
                  <span className="text-amber-400">told whole.</span>
                </SectionHeading>
                <div className="mt-10 space-y-6 text-zinc-200 text-lg leading-relaxed">
                  {show.synopsis.map((p, i) => (
                    <p key={i} data-testid={`show-synopsis-${i}`}>
                      {p}
                    </p>
                  ))}
                </div>
              </FadeIn>

              {show.sections?.length > 0 && (
                <FadeIn>
                  <SectionLabel>Inside the experience</SectionLabel>
                  <ul className="mt-8 space-y-3">
                    {show.sections.map((s, i) => (
                      <li
                        key={i}
                        data-testid={`show-section-${i}`}
                        className="border border-white/5 bg-zinc-950 p-6 hover:border-amber-400/40 transition-colors"
                      >
                        <p className="text-[10px] uppercase tracking-[0.3em] text-amber-400 font-bold">
                          Chapter {String(i + 1).padStart(2, "0")}
                        </p>
                        <h3 className="mt-3 font-display font-bold text-2xl uppercase">
                          {s.h}
                        </h3>
                        <p className="mt-3 text-zinc-300 leading-relaxed">{s.b}</p>
                      </li>
                    ))}
                  </ul>
                </FadeIn>
              )}

              {show.themes?.length > 0 && (
                <FadeIn>
                  <SectionLabel>Themes</SectionLabel>
                  <ul className="mt-6 flex flex-wrap gap-3">
                    {show.themes.map((t) => (
                      <li
                        key={t}
                        className="border border-amber-400/30 px-5 py-3 text-sm uppercase tracking-[0.2em] font-bold text-amber-400 hover:bg-amber-400/10 transition-colors"
                      >
                        {t}
                      </li>
                    ))}
                  </ul>
                </FadeIn>
              )}
            </div>

            {/* Sidebar */}
            <aside className="lg:col-span-5">
              <div className="lg:sticky lg:top-28 space-y-6">
                <FadeIn>
                  <div className="border border-amber-400/30 bg-zinc-950 overflow-hidden">
                    <div
                      className="relative aspect-square bg-cover bg-center"
                      style={{ backgroundImage: `url(${show.posterImage})` }}
                    />
                    <div className="p-7">
                      <SectionLabel>Booking</SectionLabel>
                      <p className="mt-4 font-display font-black text-3xl text-amber-400 leading-tight">
                        {show.runDates}
                      </p>
                      <ul className="mt-6 space-y-3 text-sm text-zinc-300">
                        <Detail Icon={MapPin} label={show.venue} />
                        {show.venueAddress && (
                          <Detail Icon={MapPin} label={show.venueAddress} muted />
                        )}
                        <Detail Icon={Clock} label={show.runTime} />
                        {show.ageGuidance && (
                          <Detail Icon={ShieldAlert} label={show.ageGuidance} />
                        )}
                      </ul>
                      <div className="mt-7">
                        <BookCta />
                      </div>
                    </div>
                  </div>
                </FadeIn>
              </div>
            </aside>
          </div>

          {/* ACCESSIBILITY */}
          {show.accessibility?.length > 0 && (
            <FadeIn className="mt-24 sm:mt-32">
              <SectionLabel>Accessibility</SectionLabel>
              <div className="mt-6 border border-amber-400/20 bg-zinc-950 p-8 sm:p-10">
                <div className="flex items-start gap-5">
                  <Accessibility className="h-7 w-7 text-amber-400 shrink-0 mt-1" />
                  <ul className="space-y-3 text-zinc-200">
                    {show.accessibility.map((a, i) => (
                      <li
                        key={i}
                        data-testid={`show-accessibility-${i}`}
                        className="leading-relaxed"
                      >
                        — {a}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </FadeIn>
          )}

          {/* GALLERY */}
          {show.gallery?.length > 0 && (
            <FadeIn className="mt-24 sm:mt-32">
              <SectionLabel>Gallery</SectionLabel>
              <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-3">
                {show.gallery.map((g, i) => (
                  <div
                    key={i}
                    data-testid={`show-gallery-${i}`}
                    className="relative aspect-square overflow-hidden border border-white/5 group"
                  >
                    <div
                      className="absolute inset-0 bg-cover bg-center transition-transform duration-[1500ms] group-hover:scale-110"
                      style={{ backgroundImage: `url(${g})` }}
                    />
                  </div>
                ))}
              </div>
            </FadeIn>
          )}

          {/* CREDITS */}
          {show.credits?.length > 0 && (
            <FadeIn className="mt-24 sm:mt-32">
              <SectionLabel>{show.creditsHeading || "Credits"}</SectionLabel>
              <ul className="mt-8 grid sm:grid-cols-2 gap-3">
                {show.credits.map((c, i) => (
                  <li
                    key={i}
                    className="border border-white/5 bg-zinc-950 px-5 py-4 text-zinc-300 leading-relaxed"
                  >
                    {c}
                  </li>
                ))}
              </ul>
            </FadeIn>
          )}

          {/* FINAL CTA */}
          <FadeIn className="mt-28 sm:mt-36 border-t border-white/5 pt-20">
            <div className="grid lg:grid-cols-12 gap-12 items-center">
              <div className="lg:col-span-7">
                <SectionLabel>Step inside</SectionLabel>
                <SectionHeading className="mt-6">
                  Enter the <span className="text-amber-400">story.</span>
                </SectionHeading>
                <p className="mt-6 text-zinc-300 text-lg leading-relaxed max-w-xl">
                  {show.bookingType === "external"
                    ? "Pre-booking is required. Secure your slot — capacity is intentionally limited so every audience gets the full immersive experience."
                    : "This production is currently in development. Register your interest and we'll get in touch the moment dates open."}
                </p>
              </div>
              <div className="lg:col-span-5 flex">
                <BookCta />
              </div>
            </div>
          </FadeIn>

          {/* RELATED */}
          {related.length > 0 && (
            <div className="mt-28 sm:mt-36 border-t border-white/5 pt-20">
              <FadeIn>
                <SectionLabel>More from the temple</SectionLabel>
                <h2 className="mt-6 tok-headline-xl">
                  Keep <span className="text-amber-400">exploring.</span>
                </h2>
              </FadeIn>
              <div className="mt-14 grid md:grid-cols-3 gap-5">
                {related.map((r, i) => (
                  <FadeIn key={r.slug} delay={i * 0.06}>
                    <Link
                      to={`/shows/${r.slug}`}
                      data-testid={`related-show-${r.slug}`}
                      className="group block border border-white/5 hover:border-amber-400/40 bg-zinc-950 transition-colors overflow-hidden"
                    >
                      <div className="relative aspect-[4/5] overflow-hidden">
                        <div
                          className="absolute inset-0 bg-cover bg-center transition-transform duration-[1500ms] group-hover:scale-110"
                          style={{ backgroundImage: `url(${r.posterImage})` }}
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
                        <div className="absolute inset-x-0 bottom-0 p-6">
                          <p className="text-[10px] uppercase tracking-[0.3em] text-amber-400 font-bold">
                            {r.eyebrow}
                          </p>
                          <h3 className="mt-3 font-display font-bold text-2xl leading-tight">
                            {r.title}
                          </h3>
                        </div>
                      </div>
                    </Link>
                  </FadeIn>
                ))}
              </div>
            </div>
          )}
        </Container>
      </section>
    </div>
  );
}

function StripCell({ Icon, label, value }) {
  return (
    <div className="flex items-start gap-3">
      <Icon className="h-5 w-5 mt-0.5 shrink-0" />
      <div>
        <p className="text-[10px] uppercase tracking-[0.3em] font-bold opacity-70">
          {label}
        </p>
        <p className="mt-1 font-display font-bold text-sm sm:text-base leading-tight">
          {value}
        </p>
      </div>
    </div>
  );
}

function Detail({ Icon, label, muted }) {
  return (
    <li className="flex items-start gap-3">
      <Icon className="h-4 w-4 text-amber-400 mt-0.5 shrink-0" />
      <span className={`leading-relaxed ${muted ? "text-zinc-500 text-xs" : ""}`}>
        {label}
      </span>
    </li>
  );
}
